import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  DeviceMobile, CheckCircle, AppleLogo, AndroidLogo, Broadcast, WifiHigh, Warning, Copy,
} from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { copyText } from "@/lib/clipboard";

const nfcSupported = typeof window !== "undefined" && "NDEFReader" in window;

export function NfcWriteDialog({ open, onOpenChange, presetTag = null }) {
  const { t } = useApp();
  const [step, setStep] = useState("select"); // select | writing | success | unsupported
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [written, setWritten] = useState(null);

  const tagUrl = (c) => `${window.location.origin}/t/${c}`;

  const doWrite = async (tg) => {
    if (nfcSupported) return write(tg);
    setStep("unsupported");
  };

  const write = async (tg) => {
    setStep("writing");
    try {
      const ndef = new window.NDEFReader();
      await ndef.write({ records: [{ recordType: "url", data: tagUrl(tg.code) }] });
      setWritten(tg);
      setStep("success");
    } catch {
      toast.error(t("nfcWriteFailed"));
      setStep("select");
    }
  };

  useEffect(() => {
    if (!open) return;
    setWritten(null);
    if (presetTag) {
      setTags([presetTag]);
      if (nfcSupported) write(presetTag);
      else setStep("unsupported");
      return;
    }
    setStep(nfcSupported ? "select" : "unsupported");
    setLoading(true);
    api.get("/tags")
      .then((r) => setTags((r.data || []).filter((tg) => tg.status === "ACTIVE")))
      .catch(() => setTags([]))
      .finally(() => setLoading(false));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const copyTags = presetTag ? [presetTag] : tags;
  const copyUrl = async (tg) => { await copyText(tagUrl(tg.code)); toast.success(t("urlCopied")); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="nfc-write-dialog" className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <DeviceMobile size={22} weight="duotone" />{t("nfcWriteTitle")}
          </DialogTitle>
          <DialogDescription className="mt-1.5">{t("nfcWriteAndroidHint")}</DialogDescription>
        </DialogHeader>

        {step === "select" && (
          <div className="space-y-3" data-testid="nfc-write-select">
            <p className="text-sm font-body text-gray-600">{t("nfcWriteSelectTag")}</p>
            {loading && <p className="font-body text-gray-500 text-sm">…</p>}
            {!loading && tags.length === 0 && (
              <p data-testid="nfc-write-empty" className="font-body text-gray-500 text-sm">{t("nfcWriteNoActiveTags")}</p>
            )}
            <div className="space-y-2">
              {tags.map((tg) => (
                <button key={tg.code} data-testid={`nfc-write-tag-${tg.code}`} onClick={() => write(tg)}
                  className="sb-btn w-full py-3 bg-white font-medium flex items-center justify-between gap-2 text-left">
                  <span className="flex flex-col">
                    <span className="font-heading">{tg.label || t("unnamed")}</span>
                    <span className="text-xs font-body text-gray-500">/t/{tg.code}</span>
                  </span>
                  <WifiHigh size={20} weight="bold" style={{ color: "var(--app-strong)" }} />
                </button>
              ))}
            </div>
          </div>
        )}

        {step === "writing" && (
          <div className="flex flex-col items-center gap-4 py-6" data-testid="nfc-write-writing">
            <div className="relative sb-float">
              <DeviceMobile size={70} weight="duotone" style={{ color: "var(--app-strong)" }} />
              <Broadcast size={26} weight="bold" className="absolute -right-5 top-6 sb-wobble" style={{ color: "var(--app-primary)" }} />
            </div>
            <p className="font-heading text-center text-gray-800">{t("nfcWriteHoldTag")}</p>
            <button data-testid="nfc-write-cancel" onClick={() => onOpenChange(false)}
              className="sb-btn w-full py-3 bg-white font-medium">{t("cancel")}</button>
          </div>
        )}

        {step === "success" && (
          <div className="space-y-3 text-center" data-testid="nfc-write-success">
            <CheckCircle size={64} weight="fill" className="mx-auto" style={{ color: "#22C55E" }} />
            <p className="font-heading font-semibold text-lg text-gray-900">{t("nfcWriteSuccess")}</p>
            <p className="font-body text-sm text-gray-600">{written?.label || t("unnamed")} · /t/{written?.code}</p>
            <button data-testid="nfc-write-again" onClick={() => (presetTag ? write(presetTag) : setStep("select"))}
              className="sb-btn w-full py-3 bg-white font-medium">{t("nfcWriteAgain")}</button>
            <button data-testid="nfc-write-done" onClick={() => onOpenChange(false)}
              className="sb-btn w-full py-3.5 text-white font-medium" style={{ background: "var(--app-primary)" }}>{t("done")}</button>
          </div>
        )}

        {step === "unsupported" && (
          <div className="space-y-3" data-testid="nfc-write-unsupported">
            <div className="flex items-start gap-2 rounded-2xl border-2 border-gray-800 px-3 py-2.5"
              style={{ background: "color-mix(in srgb, var(--app-primary) 12%, #fff)" }}>
              <Warning size={22} weight="bold" className="shrink-0 mt-0.5" style={{ color: "var(--app-strong)" }} />
              <span className="font-body text-sm text-gray-800">{t("nfcWriteUnsupportedText")}</span>
            </div>

            {copyTags.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("nfcWriteCopyLabel")}</p>
                {copyTags.map((tg) => (
                  <button key={tg.code} data-testid={`nfc-copy-url-${tg.code}`} onClick={() => copyUrl(tg)}
                    className="sb-btn w-full py-3 bg-white font-medium flex items-center justify-between gap-2 text-left">
                    <span className="flex flex-col min-w-0">
                      <span className="font-heading truncate">{tg.label || t("unnamed")}</span>
                      <span className="text-xs font-body text-gray-500">/t/{tg.code}</span>
                    </span>
                    <Copy size={18} weight="bold" style={{ color: "var(--app-strong)" }} />
                  </button>
                ))}
              </div>
            )}

            <a data-testid="nfc-write-ios-link" href="https://apps.apple.com/app/nfc-tools/id1252962749" target="_blank" rel="noreferrer"
              className="sb-btn w-full py-3 bg-white font-medium flex items-center justify-center gap-2"><AppleLogo size={20} weight="fill" />{t("nfcAppsIos")}</a>
            <a data-testid="nfc-write-android-link" href="https://play.google.com/store/apps/details?id=com.wakdev.wdnfc" target="_blank" rel="noreferrer"
              className="sb-btn w-full py-3 bg-white font-medium flex items-center justify-center gap-2"><AndroidLogo size={20} weight="fill" />{t("nfcAppsAndroid")}</a>
            <button data-testid="nfc-write-close" onClick={() => onOpenChange(false)}
              className="sb-btn w-full py-3.5 text-white font-medium" style={{ background: "var(--app-primary)" }}>{t("done")}</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
